const DAY_MS = 24 * 60 * 60 * 1000;

function localDate(value) {
  const text = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [year, month, day] = text.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function getScheduleFields(schedule = {}) {
  const recurrence = schedule.recurrence && typeof schedule.recurrence === 'object' ? schedule.recurrence : {};
  return {
    start: localDate(schedule.start_time || schedule.start_date || schedule.startDate || recurrence.start_time || recurrence.start_date),
    unit: String(schedule.recurrence_unit || schedule.recurrenceUnit || recurrence.unit || recurrence.recurrence_unit || '').toLowerCase(),
    interval: Math.max(1, Number(schedule.recurrence_number || schedule.recurrenceNumber || recurrence.number || recurrence.recurrence_number) || 1),
    completionDays: Number(schedule.completion_days || recurrence.completion_days)
  };
}

function scheduleFields(schedule = {}) {
  return getScheduleFields(schedule);
}

export function formatSchedulePattern(schedule = {}) {
  const { start, unit, interval, completionDays } = getScheduleFields(schedule);
  if (!start || !unit) return null;
  let pattern;
  if (unit === 'weeks') {
    const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(start);
    pattern = interval === 1 ? `Every ${weekday}` : `Every ${interval} weeks on ${weekday}`;
  } else if (unit === 'months') {
    const day = new Intl.DateTimeFormat(undefined, { day: 'numeric' }).format(start);
    pattern = interval === 1 ? `Every ${day} of the month` : `Every ${interval} months on the ${day}`;
  } else if (unit === 'days') {
    pattern = interval === 1 ? 'Every day' : `Every ${interval} days`;
  } else {
    return null;
  }
  return Number.isFinite(completionDays) && completionDays > 0
    ? `${pattern}, within ${completionDays} day${completionDays === 1 ? '' : 's'}`
    : pattern;
}

export function getNextScheduledWorkout(schedule, now = new Date()) {
  const { start, unit, interval } = scheduleFields(schedule);
  if (!start) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startKey = dayKey(start);
  for (let offset = 0; offset <= 370; offset += 1) {
    const candidate = new Date(today.getTime() + offset * DAY_MS);
    if (candidate < start) continue;
    const elapsedDays = Math.floor((candidate - start) / DAY_MS);
    const elapsedMonths = (candidate.getFullYear() - start.getFullYear()) * 12 + candidate.getMonth() - start.getMonth();
    const matches = !unit
      ? dayKey(candidate) === startKey
      : unit === 'days'
        ? elapsedDays % interval === 0
        : unit === 'weeks'
          ? candidate.getDay() === start.getDay() && Math.floor(elapsedDays / 7) % interval === 0
          : unit === 'months'
            ? candidate.getDate() === start.getDate() && elapsedMonths % interval === 0
            : dayKey(candidate) === startKey;
    if (matches) return { date: candidate, isToday: offset === 0 };
  }
  return null;
}

export function formatNextScheduledWorkout(schedule, now = new Date()) {
  const next = getNextScheduledWorkout(schedule, now);
  if (!next) return null;
  if (next.isToday) return { label: 'TODAY', isToday: true };
  const pattern = formatSchedulePattern(schedule);
  if (pattern) return { label: pattern, isToday: false };
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (dayKey(next.date) === dayKey(tomorrow)) return { label: 'Tomorrow', isToday: false };
  return {
    label: new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).format(next.date),
    isToday: false
  };
}
